import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { generatePath } from 'react-router'

import classnames from 'classnames'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { ButtonType } from 'src/apollo/sites'

import { LoadingSpinner } from 'shared/components/common/loading_spinner/LoadingSpinner'
import {
  GATE_DISMISSED_KEY,
  LOCATION_SELECTED_PARAM
} from 'public/components/default_template/online_ordering/location_selection/LocationSelectionGate'
import { t } from 'src/translations'
import { useButtonsConfig } from 'src/shared/components/common/button/useButtonsConfig'

export enum CtaDestination {
  OrderPage = 'Order Page',
  MenuPage = 'Menu Page'
}

export type LocationCtaProps = {
  shortUrl: string
  ctaDestination: CtaDestination
  rxName?: string | null
  orderPathPattern: string
  hostName: string
  diningOptionBehavior?: DiningOptionBehavior | null
  disabled?: boolean
  className?: string
  autoFocus?: boolean
}

const LocationCta = ({
  shortUrl,
  ctaDestination,
  rxName,
  orderPathPattern,
  hostName,
  diningOptionBehavior,
  disabled,
  className,
  autoFocus
}: LocationCtaProps) => {
  const linkText =
    ctaDestination === CtaDestination.OrderPage
      ? t('location-cta.order-now')
      : t('location-cta.view-menu')
  const [loading, setLoading] = useState(false)
  const linkRef = useRef<HTMLAnchorElement>(null)

  const buttonsConfig = useButtonsConfig(ButtonType.Primary)
  const style = useMemo(() => {
    return {
      borderRadius: `${buttonsConfig.borderRadius}px`,
      boxShadow: buttonsConfig.boxShadow,
      borderWidth: `${buttonsConfig.borderWidth}px`,
      borderStyle: 'solid'
    }
  }, [buttonsConfig])

  useEffect(() => {
    if (!autoFocus || disabled) {
      return
    }
    // Short timeout to give the InfoWindow node time to attach to the DOM before focusing
    const timeoutId = setTimeout(() => {
      linkRef.current?.focus()
    }, 50)
    return () => clearTimeout(timeoutId)
  }, [autoFocus, disabled])

  useEffect(() => {
    window.onpageshow = (event) => {
      if (event.persisted && loading) {
        setLoading(false)
      }
    }
  }, [setLoading, loading])

  const getHref = useCallback(() => {
    const params = new URLSearchParams()

    if (ctaDestination === CtaDestination.OrderPage) {
      params.set(
        'diningOption',
        diningOptionBehavior === DiningOptionBehavior.Delivery
          ? 'delivery'
          : 'takeout'
      )
    }

    params.set(LOCATION_SELECTED_PARAM, 'true')

    const url =
      ctaDestination === CtaDestination.OrderPage
        ? `${generatePath(orderPathPattern, {
            slug: shortUrl
          })}?${params.toString()}`
        : `/menu/${shortUrl}?${params.toString()}`
    return `https://${hostName}${url}`
  }, [
    diningOptionBehavior,
    ctaDestination,
    shortUrl,
    orderPathPattern,
    hostName
  ])

  return (
    <a
      ref={linkRef}
      href={getHref()}
      target='_parent'
      aria-label={`${linkText} ${rxName}`}
      aria-disabled={disabled}
      className={classnames(
        'orderButton',
        'button',
        'buttonLink',
        className,
        ButtonType.Primary,
        disabled ? 'disabled' : ''
      )}
      style={style}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault()
          return
        }
        try {
          sessionStorage.setItem(GATE_DISMISSED_KEY, 'true')
        } catch {
          // sessionStorage unavailable
        }
        setLoading(true)
      }}
    >
      {loading ? <LoadingSpinner size='23px' strokeWidth='10px' /> : linkText}
    </a>
  )
}

export default LocationCta
